import React, { useState } from "react";
import styles from "./static-header-editor.module.scss";
import { useI18N } from "@src/language/language-context";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { PillNavigation } from "@src/components/atoms/pill-navigation/pill-navigation";
import {
  ColorSwatchPicker,
  DEFAULT_TREND_LINE_COLOR,
  TREND_LINE_PALETTE,
  pickPaletteHex,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useSaveWidget } from "../hooks/use-save-widget";
import { Widget } from "@src/types/dashboards";

const cssClassName = "static-header-editor";

const STATIC_HEADER_FONT_SIZES = [16, 24, 32, 48] as const;
type StaticAlignment = "left" | "center" | "right";

export interface StaticHeaderEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

export const StaticHeaderEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: StaticHeaderEditorProps) => {
  const i18n = useI18N();

  const [content, setContent] = useState<string>(
    () => widget.presentation?.content ?? "",
  );
  const [fontSize, setFontSize] = useState<number>(
    () => widget.presentation?.font_size ?? 32,
  );
  const [alignment, setAlignment] = useState<StaticAlignment>(
    () => widget.presentation?.alignment ?? "left",
  );
  const [color, setColor] = useState<string>(
    () =>
      widget.presentation?.color ??
      (isPendingWidget ? pickPaletteHex() : DEFAULT_TREND_LINE_COLOR),
  );
  const [transparent, setTransparent] = useState<boolean | undefined | null>(
    () => (isPendingWidget ? true : widget.presentation?.transparent),
  );

  const texts = {
    contentLabel: i18n("modules.staticHeader.contentLabel"),
    contentPlaceholder: i18n("modules.staticHeader.contentPlaceholder"),
    fontSizeLabel: i18n("modules.staticHeader.fontSizeLabel"),
    alignmentLabel: i18n("modules.staticHeader.alignmentLabel"),
    alignmentLeft: i18n("modules.staticHeader.alignmentLeft"),
    alignmentCenter: i18n("modules.staticHeader.alignmentCenter"),
    alignmentRight: i18n("modules.staticHeader.alignmentRight"),
    fontColorLabel: i18n("modules.staticHeader.fontColorLabel"),
    transparentLabel: i18n("modules.staticHeader.transparentLabel"),
    saveButton: i18n("modules.staticHeader.saveButton"),
    cancelButton: i18n("modules.staticHeader.cancelButton"),
  };

  const validate = (): string | null => null;

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    const presentation = {
      ...widget.presentation,
      content,
      font_size: fontSize,
      alignment,
      color,
      transparent: transparent ? true : undefined,
    };
    save(
      {
        ...widget,
        image_url: widget.image_url ?? null,
        presentation,
      },
      { validate, imageFile: null },
    );
  };

  return (
    <div className={styles[`${cssClassName}__outer-wrapper`]}>
      <Input2025
        type="text"
        labelText={texts.contentLabel}
        inputPlaceHolder={texts.contentPlaceholder}
        inputValue={content}
        onChangeFunc={(e) => setContent(e.target.value)}
        inputVariant="dark-background"
      />
      <span className={styles[`${cssClassName}__field-label`]}>
        {texts.fontSizeLabel}
      </span>
      <PillNavigation
        options={STATIC_HEADER_FONT_SIZES.map((sz) => ({
          name: String(sz),
          link: String(sz),
          render: true,
        }))}
        border={true}
        activeView={String(fontSize)}
        onChangeCallback={(value) => setFontSize(Number(value))}
      />
      <span className={styles[`${cssClassName}__field-label`]}>
        {texts.alignmentLabel}
      </span>
      <PillNavigation
        options={[
          { name: texts.alignmentLeft, link: "left", render: true },
          { name: texts.alignmentCenter, link: "center", render: true },
          { name: texts.alignmentRight, link: "right", render: true },
        ]}
        border={true}
        activeView={alignment}
        onChangeCallback={(value) => setAlignment(value as StaticAlignment)}
      />
      <div className={styles[`${cssClassName}__field`]}>
        <ColorSwatchPicker
          label={texts.fontColorLabel}
          value={color}
          swatches={TREND_LINE_PALETTE}
          onChange={setColor}
        />
      </div>
      <CheckBoxWithText
        text={texts.transparentLabel}
        checkboxState={transparent}
        handleCheckBoxClick={() => setTransparent((prev) => !prev)}
      />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={texts.saveButton}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={texts.cancelButton}
          variant="secondary"
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
